/**
 * O que faz: Monta o relatório completo em páginas fixas para preview e PDF.
 * Onde usa: Modal de exportação do dashboard.
 * Conexões: DashboardReportPage, gráficos, rankings e modelo de relatório.
 * Tabelas: Nenhuma; recebe dados já carregados pelo dashboard.
 * Data: 27/07/2026.
 */

import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { DashboardReportModel } from '@/features/dashboard/report/buildDashboardReportModel';
import type { DashboardReportFormat } from '@/features/dashboard/report/dashboardReportFormats';
import { DashboardReportPage } from './DashboardReportPage';
import {
  DailyOriginChart,
  OriginConversionChart,
  ReportSectionCard,
  ResponseTimeSummary,
  WeekdayChart,
} from './DashboardReportCharts';
import { DashboardReportRankings } from './DashboardReportRankings';

interface DashboardReportDocumentProps {
  format: DashboardReportFormat;
  model: DashboardReportModel;
}

interface ReportMetricCardProps {
  label: string;
  value: string;
  detail?: string;
  accent?: boolean;
}

const ReportMetricCard = ({ label, value, detail, accent = false }: ReportMetricCardProps) => (
  <article className={`rounded-[24px] border p-5 ${accent ? 'border-[#B9E56C] bg-[#ECFBD1]' : 'border-[#E0E9D8] bg-white/90'}`}>
    <p className="text-[15px] font-bold uppercase tracking-[0.08em] text-[#5D7A3B]">{label}</p>
    <p className="mt-3 text-[33px] tracking-[-0.05em] text-[#173324]" style={{ fontFamily: 'Sora, Arial, sans-serif', fontWeight: 700 }}>{value}</p>
    {detail && <p className="mt-2 text-[16px] leading-6 text-[#68766A]">{detail}</p>}
  </article>
);

const formatCurrency = (value: number) => value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatPeriod = (model: DashboardReportModel) => {
  if (!model.startDate || !model.endDate) return model.periodLabel;
  return `${format(model.startDate, 'dd/MM/yyyy', { locale: ptBR })} até ${format(model.endDate, 'dd/MM/yyyy', { locale: ptBR })}`;
};

const SummaryMetrics = ({ model, compact = false }: { model: DashboardReportModel; compact?: boolean }) => {
  const metrics = [
    ['Total de contatos', String(model.kpis.totalContacts), 'Novos contatos no período'],
    ['Leads de anúncios', String(model.kpis.adLeads), 'Atribuição por anúncio'],
    ['Leads de links', String(model.kpis.exclusiveLinkLeads), 'Sem dupla contagem com anúncios'],
    ['Conversão', `${model.kpis.conversionRate.toLocaleString('pt-BR')}%`, `${model.kpis.convertedContacts} contatos convertidos`],
    ['Consultas agendadas', String(model.kpis.scheduledAppointments), 'Agendado ou confirmado'],
    ['Consultas realizadas', String(model.kpis.completedAppointments), 'Realizado ou pago'],
    ['Faturamento realizado', formatCurrency(model.kpis.paidRevenue), 'Consultas pagas + vendas do CRM no período'],
    ['Tempo de resposta', model.responseTime.human, 'Tempo médio humano'],
  ] as const;

  return <div className={`grid ${compact ? 'grid-cols-2 gap-4' : 'grid-cols-4 gap-5'}`}>{metrics.map(([label, value, detail], index) => (
    <ReportMetricCard key={label} label={label} value={value} detail={detail} accent={index === 3} />
  ))}</div>;
};

const AcquisitionPage = ({ format, model, pageNumber, totalPages }: { format: DashboardReportFormat; model: DashboardReportModel; pageNumber: number; totalPages: number }) => (
  <DashboardReportPage format={format} pageNumber={pageNumber} totalPages={totalPages} model={model} eyebrow="Aquisição e rastreamento" title="Canais que trazem contatos">
    <div className="grid h-full grid-rows-2 gap-7">
      <DashboardReportRankings title="Ranking de anúncios" subtitle="Top 10 por leads gerados no período." data={model.adRanking} />
      <DashboardReportRankings title="Ranking de links rastreados" subtitle="Inclui todo contato com link_name, mesmo quando também possui anúncio." data={model.linkRanking} />
    </div>
  </DashboardReportPage>
);

export const DashboardReportDocument = ({ format: reportFormat, model }: DashboardReportDocumentProps) => {
  const isMobile = reportFormat === 'mobile';
  // O A4 usa duas páginas: resumo com os dois gráficos e, depois, os rankings.
  const totalPages = isMobile ? 4 : 2;

  if (isMobile) {
    return (
      <div data-dashboard-report-document data-format={reportFormat} className="flex flex-col gap-8 bg-[#EAF0E4]">
        <DashboardReportPage format={reportFormat} pageNumber={1} totalPages={totalPages} model={model} eyebrow="Resumo executivo" title="Sua operação em números">
          <p className="mb-7 text-[20px] leading-8 text-[#617164]">Período: <strong className="text-[#173324]">{formatPeriod(model)}</strong></p>
          <SummaryMetrics model={model} compact />
        </DashboardReportPage>
        <DashboardReportPage format={reportFormat} pageNumber={2} totalPages={totalPages} model={model} eyebrow="Atendimento e conversão" title="Velocidade que não deixa lead esfriar">
          <div className="space-y-6">
            <ReportSectionCard title="Tempo médio de resposta" subtitle="Acompanhe o ritmo da equipe, IA e horário comercial."><ResponseTimeSummary model={model} /></ReportSectionCard>
            <ReportSectionCard title="Conversão por origem" subtitle="Contato convertido possui ao menos um agendamento não cancelado."><OriginConversionChart model={model} /></ReportSectionCard>
            <ReportSectionCard title="Mensagens no período" subtitle="Interações registradas no atendimento."><div className="grid grid-cols-2 gap-4"><ReportMetricCard label="Enviadas" value={String(model.kpis.sentMessages)} /><ReportMetricCard label="Recebidas" value={String(model.kpis.receivedMessages)} /></div></ReportSectionCard>
          </div>
        </DashboardReportPage>
        <DashboardReportPage format={reportFormat} pageNumber={3} totalPages={totalPages} model={model} eyebrow="Volume e oportunidade" title="Ritmo de entrada dos contatos">
          <div className="space-y-6"><ReportSectionCard title="Evolução diária" subtitle="Contatos distribuídos por origem exclusiva."><DailyOriginChart model={model} compact /></ReportSectionCard><ReportSectionCard title="Dias da semana" subtitle="Identifique o melhor momento para reforçar atendimento."><WeekdayChart model={model} compact /></ReportSectionCard></div>
        </DashboardReportPage>
        <AcquisitionPage format={reportFormat} model={model} pageNumber={4} totalPages={totalPages} />
      </div>
    );
  }

  return (
    <div data-dashboard-report-document data-format={reportFormat} className="flex flex-col gap-8 bg-[#EAF0E4]">
      <DashboardReportPage format={reportFormat} pageNumber={1} totalPages={totalPages} model={model} eyebrow="Resumo executivo" title="Sua operação em números">
        <div className="flex h-full flex-col">
          <p className="mb-6 text-[23px] leading-8 text-[#617164]">Período analisado: <strong className="text-[#173324]">{formatPeriod(model)}</strong></p>
          <SummaryMetrics model={model} />
          <div className="mt-6 grid flex-1 grid-cols-2 gap-7">
            <ReportSectionCard title="Tempo médio de resposta" subtitle="Humano, IA e horário comercial."><ResponseTimeSummary model={model} /></ReportSectionCard>
            <ReportSectionCard title="Conversão por origem" subtitle="A taxa considera contatos distintos convertidos."><OriginConversionChart model={model} /></ReportSectionCard>
          </div>
          <div className="mt-6 grid grid-cols-2 gap-7">
            <ReportSectionCard title="Evolução diária" subtitle="Contatos por anúncios, links e demais origens."><DailyOriginChart model={model} balanced /></ReportSectionCard>
            <ReportSectionCard title="Dias da semana" subtitle="Identifique o melhor momento para reforçar atendimento."><WeekdayChart model={model} balanced /></ReportSectionCard>
          </div>
        </div>
      </DashboardReportPage>
      <AcquisitionPage format={reportFormat} model={model} pageNumber={2} totalPages={totalPages} />
    </div>
  );
};
